import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createBillingReconciliationDpeAction,
  deleteBillingReconciliationDpeAction,
  listBillingReconciliationDpeActions,
  sendBillingReconciliationDpeActions,
  updateBillingReconciliationDpeAction,
} from './billingReconciliationDpeActionsApi.js';
import { scheduleBillingQueryInvalidation } from './billingReconciliationQueryInvalidation.js';

const listRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  const rows = payload?.rows || [];
  if (Array.isArray(rows) && payload && typeof payload === 'object') {
    Object.defineProperty(rows, 'meta', {
      value: {
        total: payload.total,
        returned: payload.returned,
        limit: payload.limit,
        offset: payload.offset,
        hasMore: payload.hasMore,
      },
      enumerable: false,
      configurable: true,
    });
  }
  return rows;
};

const dpeActionsKey = ({
  runId,
  varianceId,
  bac,
  queueStatus,
  actionType,
  program,
  serviceMonth,
  roundMode,
  roundNumber,
  includeSuperseded,
  limit,
  offset,
} = {}) => [
  'billingRecon',
  'dpeActions',
  runId || null,
  program || null,
  serviceMonth || null,
  roundMode || null,
  roundNumber || null,
  includeSuperseded || null,
  varianceId || null,
  bac || null,
  queueStatus || null,
  actionType || null,
  limit || null,
  offset || null,
];
const noRetry = { retry: false };
const rowsFromPayload = (payload) => (Array.isArray(payload) ? payload : payload?.rows || []);
const withRows = (payload, rows) => {
  if (Array.isArray(payload)) return rows;
  if (payload && typeof payload === 'object') {
    return {
      ...payload,
      rows,
      count: payload.count,
      total: payload.total,
      returned: payload.returned !== undefined ? rows.length : payload.returned,
    };
  }
  return rows;
};
const keyMatchesAction = (queryKey = [], action = {}) => {
  const [
    ,
    domain,
    runId,
    ,
    ,
    ,
    ,
    ,
    varianceId,
    bac,
    queueStatus,
    actionType,
  ] = queryKey;
  if (domain !== 'dpeActions') return false;
  if (runId !== action.runId) return false;
  if (varianceId && varianceId !== action.varianceId) return false;
  if (bac && bac !== action.bac) return false;
  if (queueStatus && queueStatus !== action.queueStatus) return false;
  if (actionType && actionType !== action.actionType) return false;
  return true;
};
const upsertAction = (payload, action) => {
  const rows = rowsFromPayload(payload);
  const nextRows = rows.some((row) => row.id === action.id)
    ? rows.map((row) => (row.id === action.id ? action : row))
    : [action, ...rows];
  return withRows(payload, nextRows);
};
const removeAction = (payload, id) => withRows(payload, rowsFromPayload(payload).filter((row) => row.id !== id));
const reconcileDpeActionCaches = (queryClient, action) => {
  if (!action?.runId) return;
  queryClient.getQueryCache()
    .findAll({ queryKey: ['billingRecon', 'dpeActions', action.runId] })
    .forEach((query) => {
      if (keyMatchesAction(query.queryKey, action)) {
        queryClient.setQueryData(query.queryKey, (current) => upsertAction(current, action));
      } else {
        queryClient.setQueryData(query.queryKey, (current) => removeAction(current, action.id));
      }
    });
};
const reconcileDpeActionCachesFromPayload = (queryClient, payload = {}) => {
  const actions = Array.isArray(payload?.actions) ? payload.actions : [];
  actions.forEach((action) => reconcileDpeActionCaches(queryClient, action));
};
const removeDpeActionCaches = (queryClient, { id, runId }) => {
  if (!id || !runId) return;
  queryClient.getQueryCache()
    .findAll({ queryKey: ['billingRecon', 'dpeActions', runId] })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, (current) => removeAction(current, id));
    });
};
const operatorContextKeyMatchesAction = (queryKey = [], action = {}) => {
  const [, domain, runId, bac, varianceId] = queryKey;
  if (domain !== 'operatorContext') return false;
  if (runId !== action.runId) return false;
  if (bac && bac !== action.bac) return false;
  if (varianceId && action.varianceId && varianceId !== action.varianceId) return false;
  return true;
};
const patchOperatorContextDpeActions = (bundle, patchRows) => {
  if (!bundle?.dpeActions || !Array.isArray(bundle.dpeActions.data)) return bundle;
  return {
    ...bundle,
    dpeActions: {
      ...bundle.dpeActions,
      data: patchRows(bundle.dpeActions.data),
    },
  };
};
const reconcileOperatorContextDpeActionCaches = (queryClient, action) => {
  if (!action?.runId) return;
  queryClient.getQueryCache()
    .findAll({ queryKey: ['billingRecon', 'operatorContext', action.runId] })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, (current) => (
        patchOperatorContextDpeActions(current, (rows) => (
          operatorContextKeyMatchesAction(query.queryKey, action)
            ? upsertAction(rows, action)
            : removeAction(rows, action.id)
        ))
      ));
    });
};
const removeOperatorContextDpeActionCaches = (queryClient, action) => {
  if (!action?.id || !action?.runId) return;
  queryClient.getQueryCache()
    .findAll({ queryKey: ['billingRecon', 'operatorContext', action.runId] })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, (current) => patchOperatorContextDpeActions(current, (rows) => removeAction(rows, action.id)));
    });
};
const invalidateDpeActionDependents = (queryClient, variables = {}, action = null) => {
  const queryKey = variables?.runId
    ? ['billingRecon', 'dpeActions', variables.runId]
    : ['billingRecon', 'dpeActions'];
  scheduleBillingQueryInvalidation(queryClient, { queryKey, refetchType: 'active' });
  const operatorContextKey = action?.runId && action?.bac
    ? ['billingRecon', 'operatorContext', action.runId, action.bac]
    : (variables?.runId ? ['billingRecon', 'operatorContext', variables.runId] : ['billingRecon', 'operatorContext']);
  scheduleBillingQueryInvalidation(queryClient, { queryKey: operatorContextKey, refetchType: 'active' });
};

export const useBillingReconciliationDpeActions = (params = {}, options = {}) =>
  useQuery({
    queryKey: dpeActionsKey(params),
    queryFn: ({ signal }) => listBillingReconciliationDpeActions({ ...params, signal }),
    enabled: (options.enabled ?? true) && Boolean(params.runId || (params.program && params.serviceMonth)),
    select: listRows,
    staleTime: 15 * 1000,
    ...noRetry,
    ...options,
  });

export const useCreateBillingReconciliationDpeAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createBillingReconciliationDpeAction,
    onSuccess: (data, variables) => {
      reconcileDpeActionCaches(queryClient, data);
      reconcileOperatorContextDpeActionCaches(queryClient, data);
      invalidateDpeActionDependents(queryClient, variables, data);
    },
  });
};

export const useUpdateBillingReconciliationDpeAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBillingReconciliationDpeAction,
    onSuccess: (data, variables) => {
      reconcileDpeActionCaches(queryClient, data);
      reconcileOperatorContextDpeActionCaches(queryClient, data);
      invalidateDpeActionDependents(queryClient, variables, data);
    },
  });
};

export const useDeleteBillingReconciliationDpeAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteBillingReconciliationDpeAction,
    onSuccess: (data, variables) => {
      removeDpeActionCaches(queryClient, variables);
      removeOperatorContextDpeActionCaches(queryClient, data || variables);
      invalidateDpeActionDependents(queryClient, variables, data);
    },
  });
};

export const useSendBillingReconciliationDpeActions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendBillingReconciliationDpeActions,
    onSuccess: (data, variables) => {
      reconcileDpeActionCachesFromPayload(queryClient, data);
      (Array.isArray(data?.actions) ? data.actions : []).forEach((action) => reconcileOperatorContextDpeActionCaches(queryClient, action));
      invalidateDpeActionDependents(queryClient, variables, Array.isArray(data?.actions) ? data.actions[0] : null);
    },
  });
};

export const __test = {
  dpeActionsKey,
  keyMatchesAction,
};
